<script setup lang="ts">
/**
 * NotificationBell — Header bell icon with unread badge.
 * Clicking opens a NotificationCenter modal (DialogModal) listing all notifications.
 *
 * Mounted in AppHeader. Reads from the notification Pinia store.
 * Zero SSE coupling — only subscribes to store state.
 *
 * Features:
 *   - Animated bell icon with unread count badge (unread notifications + pending recalls)
 *   - Opens NotificationCenter modal on click
 *   - Modal lists recent notifications with priority color-coding
 *   - Dedicated "Pending Recalls" section — recalls acknowledged but not yet actioned
 *   - Click a pending recall row → opens ViewRecallModal with Approve/Reject CTAs
 *   - Click a regular notification → marks as read + navigates to recall
 *   - "Mark all as read" bulk action
 */
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import { DialogModal, Icon, useToaster } from '@tms-phoenix/ui-vue';
import { useNotificationStore, type PendingRecallItem } from '@/stores';
import { getApiClient } from '@/core/services/api.service';
import { config } from '@/core/config';
import ViewRecallModal from '@/modules/recall/components/ViewRecallModal.vue';
import RejectRecallModal from '@/modules/recall/components/RejectRecallModal.vue';
import { updateHospitalRecallStatus } from '@/modules/recall/services/recall.api';
import type { Recall } from '@/modules/recall/types';
import { useNotificationContentLocalization } from '@/shared/composables/useNotificationContentLocalization';

const { t } = useTranslation();
const router = useRouter();
const notificationStore = useNotificationStore();
const toast = useToaster();
const { localizeSubject, localizeBody } = useNotificationContentLocalization();

const showModal = ref(false);
const showRecallModal = ref(false);
const showRejectModal = ref(false);
const selectedRecall = ref<Recall | null>(null);

const hasUnread = computed(() => notificationStore.hasUnread);
const unreadCount = computed(() => notificationStore.unreadCount);
const notifications = computed(() => notificationStore.notifications);
const isConnected = computed(() => notificationStore.isConnected);
const pendingRecalls = computed(() => notificationStore.pendingRecalls);
const pendingRecallCount = computed(() => notificationStore.pendingRecallCount);

/** Total badge count = unread notifications + pending recalls awaiting action */
const totalBadgeCount = computed(() => unreadCount.value + pendingRecallCount.value);
const hasBadge = computed(() => totalBadgeCount.value > 0);

function openNotificationCenter() {
  showModal.value = true;
}

async function handleNotificationClick(notification: (typeof notifications.value)[0]) {
  // Mark as read — failure is silent to the user by design (no actionable context)
  if (!notification.read_at) {
    const api = getApiClient();
    await notificationStore.markAsRead(notification.notification_id, api);
  }

  // Navigate to related entity if applicable
  if (
    (notification.reference_entity_type === 'RECALL' ||
      notification.reference_entity_type === 'PRODUCT_RECALL') &&
    notification.reference_entity_id
  ) {
    showModal.value = false;
    router.push(`/recall/${notification.reference_entity_id}/impact`);
  }
}

/** Map a PendingRecallItem from the store to the Recall view-model for ViewRecallModal */
function toRecall(item: PendingRecallItem): Recall {
  return {
    id: item.id,
    recallNumber: item.recallNumber,
    name: item.name,
    productName: item.productName,
    recallDate: item.recallDate,
    status: 'Pending',
    manufacturer: item.manufacturer,
    affectedLotNumbers: item.affectedLotNumbers,
    linkedProductId: item.linkedProductId,
    clinicalDescription: item.clinicalDescription,
    informationSource: item.informationSource,
    sourceUrl: item.sourceUrl,
  };
}

function handlePendingRecallClick(item: PendingRecallItem) {
  selectedRecall.value = toRecall(item);
  showModal.value = false;
  showRecallModal.value = true;
}

async function handleBellApprove(recall: Recall) {
  const hospitalId = config.hospitalId;
  const staffId = config.staffId;
  if (!hospitalId) return;

  try {
    await updateHospitalRecallStatus(recall.id, {
      hospital_id: hospitalId,
      status: 'APPROVED',
      acknowledged_by: staffId || undefined,
    });
    notificationStore.removePendingRecall(recall.id);
    showRecallModal.value = false;
    selectedRecall.value = null;
    toast.success(t('recall.bell.approveSuccess', 'Recall approved successfully.'));
  } catch (err) {
    console.error('[NotificationBell] Failed to approve recall:', err);
    toast.danger(t('recall.bell.approveError', 'Failed to approve recall. Please try again.'));
  }
}

function handleBellRejectOpen(recall: Recall) {
  selectedRecall.value = recall;
  showRecallModal.value = false;
  showRejectModal.value = true;
}

async function handleBellRejectConfirm(recall: Recall, reason: string) {
  const hospitalId = config.hospitalId;
  const staffId = config.staffId;
  if (!hospitalId) return;

  try {
    await updateHospitalRecallStatus(recall.id, {
      hospital_id: hospitalId,
      status: 'REJECTED',
      reason,
      acknowledged_by: staffId || undefined,
    });
    notificationStore.removePendingRecall(recall.id);
    showRejectModal.value = false;
    selectedRecall.value = null;
    toast.success(t('recall.bell.rejectSuccess', 'Recall rejected successfully.'));
  } catch (err) {
    console.error('[NotificationBell] Failed to reject recall:', err);
    toast.danger(t('recall.bell.rejectError', 'Failed to reject recall. Please try again.'));
  }
}

const isMarkingAllRead = ref(false);

async function handleMarkAllRead() {
  if (isMarkingAllRead.value) return;
  isMarkingAllRead.value = true;
  const api = getApiClient();
  try {
    await notificationStore.markAllAsRead(api);
  } catch (err) {
    console.error('[NotificationBell] Mark all as read failed:', err);
    toast.danger(t('notifications.markAllReadError', 'Failed to mark all as read. Please try again.'));
  } finally {
    isMarkingAllRead.value = false;
  }
}

/**
 * Format relative time using i18next translation keys.
 */
function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr).getTime();
  if (!Number.isFinite(date)) return '';
  const now = Date.now();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return t('reports.justNow', 'Just now');
  if (diffMin < 60) return t('notifications.time.minutesAgo', '{{count}}m ago', { count: diffMin });
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return t('notifications.time.hoursAgo', '{{count}}h ago', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  return t('notifications.time.daysAgo', '{{count}}d ago', { count: diffDays });
}

/**
 * Priority → color mapping for the left border indicator
 */
function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'CRITICAL':
      return 'border-l-red-500';
    case 'HIGH':
      return 'border-l-amber-500';
    case 'NORMAL':
      return 'border-l-blue-500';
    case 'LOW':
      return 'border-l-gray-400';
    default:
      return 'border-l-gray-300';
  }
}

/**
 * Priority label for display
 */
function getPriorityLabel(priority: string): string {
  switch (priority) {
    case 'CRITICAL':
      return t('patient.status.critical');
    case 'HIGH':
      return t('notifications.priority.high');
    case 'NORMAL':
      return t('notifications.priority.normal');
    case 'LOW':
      return t('notifications.priority.low');
    default:
      return '';
  }
}
</script>

<template>
  <div class="relative">
    <!-- Bell Button -->
    <button
      type="button"
      class="relative flex items-center rounded-lg p-2 hover:bg-gray-100 transition-colors"
      :aria-label="t('notifications.bell.openAriaLabel')"
      @click="openNotificationCenter"
    >
      <!-- Bell SVG -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6 text-gray-600"
        :class="{ 'animate-ring': hasBadge }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
        />
      </svg>

      <!-- Unread Badge -->
      <span
        v-if="hasBadge"
        class="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-sm"
      >
        {{ totalBadgeCount > 99 ? '99+' : totalBadgeCount }}
      </span>

      <!-- Connection indicator dot -->
      <span
        class="absolute bottom-1 right-1 h-2 w-2 rounded-full"
        :class="isConnected ? 'bg-green-400' : 'bg-gray-400'"
        :title="isConnected ? t('notifications.bell.liveUpdatesActive') : t('notifications.bell.reconnecting')"
      />
    </button>

    <!-- Notification Center Modal -->
    <DialogModal
      :visible="showModal"
      :title="t('notifications.center.title')"
      max-width="max-w-2xl"
      position="center"
      :show-footer="false"
      no-padding
      @update:visible="showModal = $event"
    >
      <div class="flex flex-col max-h-[70vh]">
        <!-- Modal Header Bar -->
        <div class="flex items-center justify-between border-b border-gray-200 px-6 py-3 shrink-0">
          <span class="text-sm text-gray-500">
            <template v-if="hasUnread">
              {{ t('notifications.center.unreadCount', { count: unreadCount }) }}
            </template>
            <template v-else>
              {{ t('notifications.center.allCaughtUp') }}
            </template>
          </span>
          <button
            v-if="hasUnread"
            type="button"
            class="text-xs font-medium text-primary-600 hover:text-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isMarkingAllRead"
            @click="handleMarkAllRead"
          >
            {{ t('notifications.center.markAllRead') }}
          </button>
        </div>

        <!-- Notification List -->
        <div class="flex-1 overflow-y-auto">
          <!-- Empty state — only shown when there are no notifications AND no pending recalls -->
          <div
            v-if="notifications.length === 0 && pendingRecalls.length === 0"
            class="flex flex-col items-center justify-center py-16 text-gray-400"
          >
            <svg
              class="h-12 w-12 mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="1"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
              />
            </svg>
            <p class="text-sm font-medium">
              {{ t('notifications.center.emptyTitle') }}
            </p>
            <p class="text-xs mt-1">
              {{ t('notifications.center.emptySubtitle') }}
            </p>
          </div>

          <!-- Notification rows -->
          <button
            v-for="item in notifications"
            :key="item.notification_id"
            type="button"
            :aria-label="item.read_at ? localizeSubject(item.event_type, item.subject) : t('notifications.center.unreadAriaPrefix', 'Unread: {{subject}}', { subject: localizeSubject(item.event_type, item.subject) })"
            :class="[
              'flex items-start gap-3 px-6 py-4 border-b border-gray-100 border-l-4 cursor-pointer transition-colors w-full text-left',
              item.read_at ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/40 hover:bg-blue-50/70',
              getPriorityColor(item.priority),
            ]"
            @click="handleNotificationClick(item)"
          >
            <!-- Priority indicator icon -->
            <div class="shrink-0 mt-0.5">
              <span
                v-if="item.priority === 'CRITICAL'"
                class="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600"
              >
                <Icon
                  name="CircleExclamation"
                  size-class="h-4 w-4"
                  color="text-red-600"
                />
              </span>
              <span
                v-else-if="item.priority === 'HIGH'"
                class="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600"
              >
                <Icon
                  name="CircleExclamation"
                  size-class="h-4 w-4"
                  color="text-amber-600"
                />
              </span>
              <span
                v-else
                class="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600"
              >
                <Icon
                  name="InfoCircle"
                  size-class="h-4 w-4"
                  color="text-blue-900"
                />
              </span>
            </div>

            <!-- Notification content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <p
                  class="text-sm text-gray-900 truncate"
                  :class="{ 'font-semibold': !item.read_at }"
                >
                  {{ localizeSubject(item.event_type, item.subject) }}
                </p>
                <span
                  v-if="!item.read_at"
                  class="shrink-0 inline-block h-2 w-2 rounded-full bg-blue-500"
                />
              </div>
              <p class="text-xs text-gray-500 line-clamp-2 mt-1">
                {{ localizeBody(item.event_type, item.rendered_body) }}
              </p>
              <div class="flex items-center gap-3 mt-1.5">
                <span class="text-[11px] text-gray-400">
                  {{ formatRelativeTime(item.created_at) }}
                </span>
                <span
                  class="text-[11px] font-medium"
                  :class="{
                    'text-red-600': item.priority === 'CRITICAL',
                    'text-amber-600': item.priority === 'HIGH',
                    'text-blue-600': item.priority === 'NORMAL',
                    'text-gray-400': item.priority === 'LOW',
                  }"
                >
                  {{ getPriorityLabel(item.priority) }}
                </span>
              </div>
            </div>

            <!-- Navigate arrow -->
            <div
              v-if="item.reference_entity_type === 'RECALL'"
              class="shrink-0 mt-2"
            >
              <Icon
                name="AngleRight"
                size-class="h-4 w-4"
                color="text-gray-400"
              />
            </div>
          </button>
        </div>

        <!-- Pending Recalls Section -->
        <div
          v-if="pendingRecalls.length > 0"
          class="shrink-0 border-t border-amber-200"
        >
          <!-- Section header -->
          <div class="flex items-center gap-2 bg-amber-50 px-6 py-2.5">
            <Icon
              name="CircleExclamation"
              size-class="h-4 w-4 shrink-0"
              color="text-amber-600"
            />
            <span class="text-xs font-semibold text-amber-700 uppercase tracking-wide">
              {{ t('recall.bell.pendingSection', 'Pending Recalls') }}
            </span>
            <span class="ml-auto inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">
              {{ pendingRecalls.length }}
            </span>
          </div>

          <!-- Pending recall rows -->
          <button
            v-for="recall in pendingRecalls"
            :key="recall.id"
            type="button"
            class="flex items-start gap-3 px-6 py-4 border-b border-amber-100 border-l-4 border-l-amber-500 bg-amber-50/40 hover:bg-amber-50/70 cursor-pointer transition-colors w-full text-left"
            :aria-label="t('recall.bell.recallRowAriaLabel', 'Pending recall: {{name}}', { name: recall.name })"
            @click="handlePendingRecallClick(recall)"
          >
            <!-- Recall icon -->
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600 shrink-0 mt-0.5">
              <Icon
                name="IconWarning"
                size-class="h-4 w-4"
                variant="stroke"
                color="text-amber-600"
              />
            </span>

            <!-- Recall info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <p class="text-sm font-semibold text-gray-900 truncate">
                  {{ recall.name }}
                </p>
                <span class="shrink-0 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                  {{ t('common.pending') }}
                </span>
              </div>
              <p class="text-xs text-gray-500 truncate mt-0.5">
                {{ recall.recallNumber }} · {{ recall.productName }}
              </p>
              <p class="text-[11px] text-amber-600 mt-1 font-medium">
                {{ t('recall.bell.actionRequired', 'Action required — click to review') }}
              </p>
            </div>

            <!-- Arrow -->
            <div class="shrink-0 mt-2">
              <Icon
                name="AngleRight"
                size-class="h-4 w-4"
                color="text-amber-400"
              />
            </div>
          </button>
        </div>
      </div>
    </DialogModal>

    <!-- Recall Detail Modal (opened from a pending recall row) -->
    <ViewRecallModal
      :visible="showRecallModal"
      :recall="selectedRecall"
      @update:visible="showRecallModal = $event"
      @approve="handleBellApprove"
      @reject="handleBellRejectOpen"
    />

    <!-- Reject Reason Modal -->
    <RejectRecallModal
      :visible="showRejectModal"
      :recall="selectedRecall"
      @update:visible="showRejectModal = $event"
      @confirm="handleBellRejectConfirm"
    />
  </div>
</template>

<style scoped>
/* Bell ring animation for unread state */
@keyframes ring {
  0% { transform: rotate(0deg); }
  10% { transform: rotate(15deg); }
  20% { transform: rotate(-12deg); }
  30% { transform: rotate(9deg); }
  40% { transform: rotate(-6deg); }
  50% { transform: rotate(3deg); }
  60% { transform: rotate(0deg); }
  100% { transform: rotate(0deg); }
}

.animate-ring {
  animation: ring 2s ease-in-out 1;
  transform-origin: top center;
}
</style>
